import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { number, bool, string, func, object, oneOfType } from "prop-types";
import FontAwesome from "react-fontawesome";
import { Row, Col, Button, Nav, Tab, Table, Modal } from "react-bootstrap";
import Loader from "react-loader-advanced";
import { ThreeBounce } from "better-react-spinkit";
import { format, parseISO } from "date-fns";
import {
  getCandidateRatings,
  rateCandidate,
  editCandidateRating,
  getJobRating,
} from "../actions";
import RateCandidate from "./RateCandidate";

const Ratings = ({
  candidateName,
  userRatedCandidate,
  candidateAvgRating,
  candidateId,
  candidateStageId,
  jobId,
  candidatesRatings,
  jobRatings,
  changeAction,
  onGetCandidateRatings,
  onRateCandidate,
  onEditCandidateRating,
  onGetJobRating,
  userData,
  postRateCandidatesLoading,
  postRateCandidatesError,
}) => {
  const [showRateCandidateScreen, setShowRateCandidateScreen] = useState(false);
  const [userCandidateRating, setUserCandidateRating] = useState(false);

  // Component did mount.
  useEffect(() => {
    onGetCandidateRatings(candidateId);
    if (!jobRatings || !jobRatings.get(jobId)) {
      onGetJobRating(jobId);
      setShowRateCandidateScreen(true);
    } else {
      setShowRateCandidateScreen(
        !determineRatingPermission(jobRatings.get(jobId).get(0))
      );
    }

    // if there are no ratings, even the admin needs to be directed to the rate candidate page
    if (
      candidatesRatings &&
      candidatesRatings.size > 0 &&
      (!candidatesRatings.get(`${candidateId}`) ||
        candidatesRatings.get(`${candidateId}`).size < 1)
    ) {
      setShowRateCandidateScreen(true);
    }
  }, []);

  // when candidatesRatings are returned, check to see if the user has rated the candidate, and update state accordingly
  useEffect(() => {
    if (
      candidatesRatings &&
      candidatesRatings.size > 0 &&
      candidatesRatings.get(`${candidateId}`) &&
      candidatesRatings.get(`${candidateId}`).size > 0 &&
      userData
    ) {
      candidatesRatings.get(`${candidateId}`).forEach((candidateRatingObj) => {
        if (
          `${candidateRatingObj.get("userAccountId")}` ===
          `${userData.userAccountId}`
        ) {
          setUserCandidateRating(candidateRatingObj);
        }
      });
    }

    // if there are no ratings, even the admin needs to be directed to the rate candidate page
    if (
      candidatesRatings &&
      candidatesRatings.size > 0 &&
      (!candidatesRatings.get(`${candidateId}`) ||
        candidatesRatings.get(`${candidateId}`).size < 1)
    ) {
      setShowRateCandidateScreen(true);
    }
  }, [candidatesRatings]);

  // if the ratings endpoint is being returned, check if user has permission to view ratings and show rating screen accordingly
  useEffect(() => {
    if (jobRatings.get(jobId)) {
      setShowRateCandidateScreen(
        !determineRatingPermission(jobRatings.get(jobId).first())
      );
    }

    // if candidate ratings had no ratings previously, but they are being returned, update state to show ratings if user has permission
    if (
      jobRatings &&
      jobRatings.get(jobId) &&
      candidatesRatings.get(`${candidateId}`) &&
      candidatesRatings.get(`${candidateId}`).size > 0 &&
      (!candidatesRatings.get(`${candidateId}`) ||
        candidatesRatings.get(`${candidateId}`).size < 1)
    ) {
      setShowRateCandidateScreen(
        !determineRatingPermission(jobRatings.get(jobId).first())
      );
    }
  }, [jobRatings]);

  const closeModal = () => {
    changeAction(false);
  };

  const toggleModalView = () => {
    setShowRateCandidateScreen(!showRateCandidateScreen);
  };

  const determineRatingPermission = (ratingSystem) => {
    if (!userData || !ratingSystem) {
      return false;
    }
    let showPage = userRatedCandidate;
    if (!showPage) {
      if (userData && userData.roleId === 1) {
        switch (ratingSystem.get("ratingView")) {
          case "primaries":
          case "all":
            showPage = true;
            break;
          case "hidden":
            showPage = false;
            break;
          default:
            break;
        }
      } else {
        switch (ratingSystem.get("ratingView")) {
          case "primaries":
          case "hidden":
            showPage = false;
            break;
          case "all":
            showPage = true;
            break;
          default:
            break;
        }
      }
    }
    return showPage;
  };

  // ------------------ THIS IS WHERE THE RENDERING STARTS -----------------------
  const loadingModal =
    !candidatesRatings ||
    candidatesRatings.size < 1 ||
    !jobRatings ||
    jobRatings.size < 1 ||
    !jobRatings.get(jobId);

  const ratingSystem = !loadingModal && jobRatings.get(jobId).first();
  const internal = userData && userData.internal;
  const showPage = determineRatingPermission(ratingSystem);
  const RatingNavItems = [];
  const RatingNavContent = [];
  const TableRows = [];
  if (
    candidatesRatings.get(`${candidateId}`) &&
    candidatesRatings.get(`${candidateId}`).size > 0 &&
    !loadingModal
  ) {
    // add the navitem and tab pane for the overall_rating, since it is not included in the questions array
    RatingNavItems.push(
      <Nav.Item>
        <Nav.Link key="overall_nav" eventKey="rating_overall">
          Overall Rating
        </Nav.Link>
      </Nav.Item>
    );
    const ratingType = ratingSystem.get("ratingType");
    candidatesRatings.get(`${candidateId}`).forEach((candidateRatingObj) => {
      let RatingDisplay = null;
      const assignedRating = candidateRatingObj.get("rating_overall");
      if (
        assignedRating !== undefined ||
        assignedRating !== null ||
        !isNaN(Number(assignedRating))
      ) {
        switch (ratingType) {
          case "stars": {
            const starsArray = [];
            for (let i = 0; i < Math.floor(assignedRating); i += 1) {
              starsArray.push(<FontAwesome name="star" key={i} />);
            }
            const remainder = assignedRating - Math.floor(assignedRating);
            if (remainder > 0.25 && remainder < 0.75) {
              starsArray.push(<FontAwesome name="star-half-o" key="half" />);
            } else if (remainder > 0.75) {
              starsArray.push(<FontAwesome name="star" key="full" />);
            }
            RatingDisplay = starsArray;
            break;
          }
          case "ten": {
            let remainder = assignedRating - Math.floor(assignedRating);
            if (remainder < 0.25) {
              remainder = 0;
            } else if (remainder >= 0.25 && remainder < 0.75) {
              remainder = 0.5;
            } else {
              remainder = 1;
            }
            RatingDisplay = (
              <div>
                <FontAwesome name="star" key="full" />{" "}
                {Math.floor(assignedRating) + remainder}
              </div>
            );
            break;
          }
          default: {
            RatingDisplay =
              assignedRating > 0 ? (
                <FontAwesome name="thumbs-up" key="thumbs_up" />
              ) : (
                <FontAwesome name="thumbs-down" key="thumbs_down" />
              );
          }
        }
      }
      TableRows.push(
        <tr key={`rating_overall_${candidateRatingObj.get("userAccountId")}}`}>
          <td>{RatingDisplay}</td>
          <td>
            {candidateRatingObj.get("firstName")}{" "}
            {candidateRatingObj.get("lastName")}
            {" - "}
            {format(
              parseISO(candidateRatingObj.get("dateRated")),
              "MMM dd, yyyy h:m a"
            )}
          </td>
          <td>{candidateRatingObj.get("comment_overall")}</td>
        </tr>
      );
    });

    let AvgOverallDisplay = null;
    switch (ratingType) {
      case "stars": {
        const starsArray = [];
        for (let i = 0; i < Math.floor(candidateAvgRating); i += 1) {
          starsArray.push(<FontAwesome name="star" key={i} />);
        }
        const remainder = candidateAvgRating - Math.floor(candidateAvgRating);
        if (remainder > 0.25 && remainder < 0.75) {
          starsArray.push(<FontAwesome name="star-half-o" key="half" />);
        } else if (remainder > 0.75) {
          starsArray.push(<FontAwesome name="star" key="full" />);
        }
        AvgOverallDisplay = starsArray;
        break;
      }
      case "ten": {
        let remainder = candidateAvgRating - Math.floor(candidateAvgRating);
        if (remainder < 0.25) {
          remainder = 0;
        } else if (remainder >= 0.25 && remainder < 0.75) {
          remainder = 0.5;
        } else {
          remainder = 1;
        }
        AvgOverallDisplay = (
          <div>
            <FontAwesome name="star" key="full" />{" "}
            {Math.floor(candidateAvgRating) + remainder}
          </div>
        );
        break;
      }
      default: {
        AvgOverallDisplay =
          candidateAvgRating > 0 ? (
            <FontAwesome name="thumbs-up" key="thumbs_up" />
          ) : (
            <FontAwesome name="thumbs-down" key="thumbs_down" />
          );
      }
    }
    RatingNavContent.push(
      <Tab.Pane key="rating_overall" eventKey="rating_overall" mountOnEnter>
        <Row>
          <Col lg={12} md={12} sm={12}>
            <h4>Average Overall Rating:</h4>
            <div className="text-center">{AvgOverallDisplay}</div>
            <hr />
            <h4>Individual Ratings:</h4>
            <Table responsive>
              <thead>
                <tr>
                  <th>Rating</th>
                  <th>Rated By</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>{TableRows}</tbody>
            </Table>
          </Col>
        </Row>
      </Tab.Pane>
    );
    // add the navitem and tabpane for every question in the rating system
    if (ratingSystem.get("questions")) {
      ratingSystem
        .get("questions")
        .get("data")
        .forEach((questionObject) => {
          const CustomTableRows = [];
          let customAverageRating = 0;
          let customRatingCount = 0;
          let customPositiveRatingCount = 0;
          let customNegativeRatingCount = 0;
          RatingNavItems.push(
            <Nav.Item>
              <Nav.Link
                key={questionObject.get("ratings_questions_id")}
                eventKey={questionObject.get("ratings_questions_id")}
              >
                {questionObject.get("ratingPrompt").slice(0, 15)}
                {questionObject.get("ratingPrompt").length > 15 ? "..." : ""}
              </Nav.Link>
            </Nav.Item>
          );
          const questionNumber = questionObject.get("ratingQuestionNumber");
          candidatesRatings.get(`${candidateId}`).forEach((ratingObject) => {
            let CustomRatingDisplay = null;
            const rating = ratingObject.get(`rating_${questionNumber}`);
            if (
              rating !== undefined ||
              rating !== null ||
              !isNaN(Number(rating))
            ) {
              switch (ratingType) {
                case "stars": {
                  customRatingCount += 1;
                  customAverageRating =
                    (customAverageRating * (customRatingCount - 1) + rating) /
                    customRatingCount;

                  const starsArray = [];
                  for (let i = 0; i < Math.floor(rating); i += 1) {
                    starsArray.push(<FontAwesome name="star" key={i} />);
                  }
                  const remainder = rating - Math.floor(rating);
                  if (remainder > 0.25 && remainder < 0.75) {
                    starsArray.push(
                      <FontAwesome name="star-half-o" key="half" />
                    );
                  } else if (remainder > 0.75) {
                    starsArray.push(<FontAwesome name="star" key="full" />);
                  }

                  CustomRatingDisplay = starsArray;
                  break;
                }
                case "ten": {
                  customRatingCount += 1;
                  customAverageRating =
                    (customAverageRating * (customRatingCount - 1) + rating) /
                    customRatingCount;

                  let remainder = rating - Math.floor(rating);
                  if (remainder < 0.25) {
                    remainder = 0;
                  } else if (remainder >= 0.25 && remainder < 0.75) {
                    remainder = 0.5;
                  } else {
                    remainder = 1;
                  }

                  CustomRatingDisplay = (
                    <div>
                      <FontAwesome name="star" key="full" />{" "}
                      {Math.floor(rating) + remainder}
                    </div>
                  );
                  break;
                }
                default: {
                  if (rating > 0) {
                    customPositiveRatingCount += 1;
                  } else {
                    customNegativeRatingCount += 1;
                  }

                  CustomRatingDisplay =
                    rating > 0 ? (
                      <FontAwesome name="thumbs-up" key="thumbs_up" />
                    ) : (
                      <FontAwesome name="thumbs-down" key="thumbs_down" />
                    );
                }
              }
            }
            CustomTableRows.push(
              <tr
                key={`rating_${questionNumber}_${ratingObject.get(
                  "userAccountId"
                )}}`}
              >
                <td>{CustomRatingDisplay}</td>
                <td>
                  {ratingObject.get("firstName")} {ratingObject.get("lastName")}
                  {" - "}
                  {format(
                    parseISO(ratingObject.get("dateRated")),
                    "MMM dd, yyyy h:m a"
                  )}
                </td>
                <td>{ratingObject.get(`comment_${questionNumber}`)}</td>
              </tr>
            );
          });

          let QuestionStars = null;
          switch (ratingType) {
            case "stars": {
              const starsArray = [];
              for (let i = 0; i < Math.floor(customAverageRating); i += 1) {
                starsArray.push(<FontAwesome name="star" key={i} size="4x" />);
              }

              const remainder =
                customAverageRating - Math.floor(customAverageRating);
              if (remainder > 0.25 && remainder < 0.75) {
                starsArray.push(<FontAwesome name="star-half-o" key="half" />);
              } else if (remainder > 0.75) {
                starsArray.push(<FontAwesome name="star" key="full" />);
              }

              QuestionStars = starsArray;
              break;
            }
            case "ten": {
              let remainder =
                customAverageRating - Math.floor(customAverageRating);
              if (remainder < 0.25) {
                remainder = 0;
              } else if (remainder >= 0.25 && remainder < 0.75) {
                remainder = 0.5;
              } else {
                remainder = 1;
              }

              QuestionStars = (
                <div>
                  <FontAwesome name="star" key="full" />{" "}
                  {Math.floor(customAverageRating) + remainder}
                </div>
              );
              break;
            }
            default: {
              QuestionStars = (
                <div>
                  <FontAwesome name="thumbs-down" key="thumbs_down" />
                  {customNegativeRatingCount}{" "}
                  <FontAwesome name="thumbs-up" key="thumbs_up" />
                  {customPositiveRatingCount}
                </div>
              );
            }
          }

          RatingNavContent.push(
            <Tab.Pane
              key={questionObject.get("ratings_questions_id")}
              eventKey={questionObject.get("ratings_questions_id")}
              mountOnEnter
            >
              <Row>
                <Col lg={12} md={12} sm={12}>
                  <h4>Average {questionObject.get("ratingPrompt")}:</h4>
                  <div className="text-center">{QuestionStars}</div>
                  <hr />
                  <h4>Individual Ratings:</h4>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Rating</th>
                        <th>Rated By</th>
                        <th>Comments</th>
                      </tr>
                    </thead>
                    <tbody>{CustomTableRows}</tbody>
                  </Table>
                </Col>
              </Row>
            </Tab.Pane>
          );
        });
    }
  } else {
    RatingNavContent.push(
      <div key="no_ratings">
        No existing ratings for this candidate. Rate the candidate by clicking
        "Rate" below:
      </div>
    );
  }
  return showRateCandidateScreen && !loadingModal ? (
    <RateCandidate
      candidateId={candidateId}
      candidateStageId={candidateStageId}
      candidateName={candidateName}
      candidatesRatings={candidatesRatings}
      closeModal={closeModal}
      onRateCandidate={onRateCandidate}
      onEditCandidateRating={onEditCandidateRating}
      ratingSystem={ratingSystem}
      toggleModalView={toggleModalView}
      userCandidateRating={userCandidateRating}
      internal={internal}
      postRateCandidatesLoading={postRateCandidatesLoading}
      postRateCandidatesError={postRateCandidatesError}
    />
  ) : (
    <div>
      <Modal.Header>
        <h4>Rating Details for: {candidateName}</h4>
      </Modal.Header>
      <Loader
        show={loadingModal}
        message={<ThreeBounce color="#1F469A" size={50} />}
        contentBlur={30}
        messageStyle={{ height: "100px" }}
        backgroundStyle={{
          backgroundColor: "rgba( 0 0 0 0)",
        }}
      >
        <Modal.Body>
          {showPage ? (
            <div>
              <Row>
                <Col lg={12} md={12} sm={12}>
                  <Tab.Container
                    id="ratings-details-left-nav"
                    defaultActiveKey="rating_overall"
                  >
                    <Row>
                      <Col lg={3} md={3} sm={3}>
                        <Nav variant="pills" className="flex-column">
                          {RatingNavItems}
                        </Nav>
                      </Col>
                      <Col lg={9} md={9} sm={9}>
                        <Tab.Content>
                          {!loadingModal && RatingNavContent}
                        </Tab.Content>
                      </Col>
                    </Row>
                  </Tab.Container>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              You must rate this candidate before viewing all ratings by
              clicking "Rate" below:
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            className="btn-cancel"
            onClick={closeModal}
            id="exitUpdateRating"
          >
            Exit
          </Button>
          <Button onClick={toggleModalView} variant="primary" id="updateRating">
            {userRatedCandidate ? "Update Your Rating" : "Rate"}
          </Button>
        </Modal.Footer>
      </Loader>
    </div>
  );
};

Ratings.propTypes = {
  candidateName: string,
  userRatedCandidate: bool,
  candidateAvgRating: oneOfType([number, string, object]),
  candidateId: oneOfType([string, bool]),
  candidateStageId: oneOfType([string, number]),
  jobId: oneOfType([string, number]),
  candidatesRatings: object,
  jobRatings: object,
  changeAction: func,
  onGetCandidateRatings: func,
  onRateCandidate: func,
  onEditCandidateRating: func,
  onGetJobRating: func,
  userData: object,
  postRateCandidatesLoading: bool,
  postRateCandidatesError: bool,
};

const mapStateToProps = (state) => ({
  candidatesRatings: state.get("results").get("candidatesRatings"),
  jobRatings: state.get("results").get("jobRatings"),
  userData: state.get("App").get("userData"),
  postRateCandidatesLoading: state
    .get("results")
    .get("postRateCandidatesLoading"),
  postRateCandidatesError: state.get("results").get("postRateCandidatesError"),
});
const mapDispatchToProps = (dispatch) => ({
  onGetCandidateRatings: (candidateId) =>
    dispatch(getCandidateRatings(candidateId)),
  onRateCandidate: (ratingInfo, stageId, internal) =>
    dispatch(rateCandidate(ratingInfo, stageId, internal)),
  onEditCandidateRating: (ratingInfo, stageId) =>
    dispatch(editCandidateRating(ratingInfo, stageId)),
  onGetJobRating: (jobId) => dispatch(getJobRating(jobId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Ratings);
